import Highcharts, { GradientColorObject, PatternObject } from 'highcharts';
import { TimelineDataPoint } from "../../changeTracking/api/fetchTimelineData";
import { ChartCompetitor, TimeseriesDataPoint } from "../types";

export function generateSeriesData<T extends TimelineDataPoint>(
  sortedData: T[],
  showPrice: boolean
) {
  return {
    primary: sortedData.map(d => ({
      x: new Date(d.date).getTime(),
      y: showPrice ? d.price : d.ranking
    })),
    compset: sortedData.map(d => ({
      x: new Date(d.date).getTime(),
      y: showPrice ? d.compsetMedianPrice : d.compsetRanking
    })),
    totalProperties: sortedData.map(d => ({
      x: new Date(d.date).getTime(),
      y: d.totalProperties
    }))
  };
}

export type CompetitorColors = Record<number, string | GradientColorObject | PatternObject>;

export const getCompetitorSeries = (
  propertyDataPoints: TimeseriesDataPoint[],
  competitors: ChartCompetitor[],
  competitorColors: CompetitorColors,
  valueProperty: 'price' | 'rank'
): Highcharts.SeriesOptionsType[] => {
  return competitors.map(competitor => {
    const color = competitorColors[competitor.property_id];

    // Use all data points regardless of rank_group
    const dataPoints = competitor.data_points;

    // Create a map of date to value for quick lookup
    // If a date has multiple values (from different rank_groups), use the average
    const dateValueMap = new Map();

    dataPoints.forEach(point => {
      const date = point.date;
      const value = point[valueProperty];

      if (!dateValueMap.has(date)) {
        dateValueMap.set(date, { sum: value, count: 1 });
      } else {
        const existing = dateValueMap.get(date);
        existing.sum += value;
        existing.count += 1;
        dateValueMap.set(date, existing);
      }
    });

    // Convert to series data with averages for dates with multiple values
    const seriesData = propertyDataPoints.map(m => {
      const dateValue = dateValueMap.get(m.date);
      if (!dateValue) return null;

      // Calculate average if we have multiple values for this date
      const avgValue = dateValue.sum / dateValue.count;
      return [new Date(m.date).getTime(), avgValue];
    }).filter(Boolean);

    return {
      name: competitor.name,
      type: 'line' as const,
      data: seriesData,
      color: color,
      lineWidth: 1,
      opacity: 0.7,
      marker: {
        symbol: 'circle',
        radius: 2,
        enabled: false
      },
      connectNulls: true,
      boostThreshold: 1,
      zIndex: 2
    } as Highcharts.SeriesLineOptions;
  }).filter(series => series.data.length > 0); // Only include series with data
};

// Generate competitor colors
export const generateCompetitorColors = (competitors: ChartCompetitor[]): CompetitorColors => {
  const colors: CompetitorColors = {};
  const baseColors = Highcharts.getOptions().colors || [];

  // Map all competitors to colors, ensuring consistent coloring
  competitors.forEach((competitor, index) => {
    colors[competitor.property_id] = baseColors[(index + 3) % baseColors.length];
  });

  return colors;
};
